/**
 * Uploads (Sales records header): every sales file that still has rows, newest first, with who uploaded it,
 * its dates, platforms, rows, units and value. Admins and managers can Undo a file: all of its rows are
 * removed (marketplace API rows and manual records are never touched) and the same file can be uploaded again.
 * Files with rows dated after today are flagged.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, History, Undo2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { LoadError, errorMessage } from '../LoadError';
import { platformLabel } from '../../config/platforms';
import { formatDateRange, number } from './format';
import { queryString, useAbortableGet } from './useAbortableGet';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const when = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value)
    : date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const rupees = value => `₹${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;
const dates = (from, to) => (from && to ? formatDateRange(from, to, true) : '—');
const undoneText = e => {
  if (e.status === 'RUNNING') return 'undo in progress';
  if (e.status === 'PARTIAL') return `undo stopped part-way (${number(e.rows)} of ${number(e.expected_rows)} rows removed); if the file is still listed, undo it again`;
  if (e.status === 'INTERRUPTED') return 'undo was cut off; if the file is still listed, undo it again';
  return `${number(e.rows)} rows removed`;
};

export const SalesUploadsDialog = ({ country, canUndo, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const opener = useRef(null);   // focus returns here when the dialog closes
  const undoButton = useRef(null);   // and to the row's Undo button when the confirmation closes
  const dialogBody = useRef(null);
  const state = useAbortableGet(open ? `${API}/sales/uploads?${queryString({ country })}` : null, refresh);
  const uploads = state.data?.uploads || [];
  const undone = state.data?.undone || [];

  const undo = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/sales/uploads/undo`, { fingerprint: confirm.fingerprint, rows: confirm.rows, country });
      toast.success(`Removed ${number(data.rows)} rows from ${data.filename || 'the file'}`);
      if (data.reimport?.length) {
        toast.warning('Import these files again to restore rows', {
          duration: 15000,
          description: `${data.reimport.map(f => f.filename).join(', ')} left out rows because "${data.filename}" already had them.`,
        });
      }
      setConfirm(null);
      setRefresh(k => k + 1);
      onChanged?.();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not undo this upload'));
      // The list shows the current counts; part of the file may be gone, so the page refreshes as well.
      setConfirm(null);
      setRefresh(k => k + 1);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button ref={opener} variant="outline" className="h-9 text-[13px]" onClick={() => setOpen(true)} data-testid="sales-uploads-button">
        <History className="mr-2 h-4 w-4" aria-hidden="true" /> Uploads
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent ref={dialogBody} className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-4xl overflow-y-auto" data-testid="sales-uploads-dialog"
          onCloseAutoFocus={e => { e.preventDefault(); opener.current?.focus(); }}>
          <DialogHeader>
            <DialogTitle className="text-[17px] font-semibold">Uploaded sales files</DialogTitle>
            <DialogDescription>
              Files that still have rows, newest first.{canUndo ? ' Undo removes every row a file added; you can then upload a corrected file.' : ''}
              {' '}Marketplace API sales and records added by hand are not listed here.
            </DialogDescription>
          </DialogHeader>
          {state.error ? <LoadError what="the upload history" error={state.error} onRetry={() => setRefresh(k => k + 1)} retrying={state.loading} />
            : state.loading && !state.data ? <p className="py-6 text-center text-[13px] text-gray-500">Loading…</p>
              : uploads.length === 0 ? <p className="py-6 text-center text-[13px] text-gray-500" data-testid="sales-uploads-empty">No uploaded files have rows in this country.</p>
                : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-[13px]" data-testid="sales-uploads-table">
                      <thead>
                        <tr className="border-b text-left text-[12px] text-gray-500">
                          <th className="py-2 pr-3 font-medium">File</th>
                          <th className="py-2 pr-3 font-medium">Uploaded</th>
                          <th className="py-2 pr-3 font-medium">Sales dates</th>
                          <th className="py-2 pr-3 text-right font-medium">Rows</th>
                          <th className="py-2 pr-3 text-right font-medium">Units</th>
                          <th className="py-2 pr-3 text-right font-medium">Value</th>
                          {canUndo && <th className="py-2 font-medium"><span className="sr-only">Undo</span></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {uploads.map(u => (
                          <tr key={u.fingerprint} className="border-b align-top" data-testid="sales-upload-row">
                            <td className="py-2 pr-3">
                              <div className="max-w-[220px] truncate font-medium text-gray-900" title={u.filename}>{u.filename}</div>
                              <div className="text-[11px] text-gray-500">{u.platforms.map(platformLabel).join(', ')}</div>
                            </td>
                            <td className="py-2 pr-3 text-gray-700">
                              {when(u.imported_at)}
                              <div className="text-[11px] text-gray-500">{u.imported_by || 'Before uploads were tracked'}</div>
                            </td>
                            <td className="py-2 pr-3 text-gray-700">
                              {dates(u.date_from, u.date_to)}
                              {u.future_dated && (
                                <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700" data-testid="sales-upload-future">
                                  <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Has future dates
                                </div>
                              )}
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums">{number(u.rows)}</td>
                            <td className="py-2 pr-3 text-right tabular-nums">{number(u.units)}</td>
                            <td className="py-2 pr-3 text-right tabular-nums">{rupees(u.value)}</td>
                            {canUndo && (
                              <td className="py-2 text-right">
                                <Button variant="outline" size="sm" className="h-8 text-[12px]" onClick={e => { undoButton.current = e.currentTarget; setConfirm(u); }} data-testid="sales-upload-undo">
                                  <Undo2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Undo
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
          {undone.length > 0 && (
            <section className="mt-2" aria-labelledby="sales-uploads-undone">
              <h3 id="sales-uploads-undone" className="text-[12px] font-semibold text-gray-700">Recent undos</h3>
              <ul className="mt-1 space-y-0.5 text-[12px] text-gray-600">
                {undone.slice(0, 5).map(e => (
                  <li key={`${e.fingerprint}-${e.at}`} data-testid="sales-upload-undone">
                    {e.filename || 'File'} · {dates(e.date_from, e.date_to)} · {undoneText(e)} · {when(e.at)} by {e.by}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog open={Boolean(confirm)} onOpenChange={next => { if (!next && !busy) setConfirm(null); }}>
        <AlertDialogContent onCloseAutoFocus={e => {
          e.preventDefault();
          (undoButton.current?.isConnected ? undoButton.current : dialogBody.current)?.focus();
        }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo this upload?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm && `This removes all ${number(confirm.rows)} rows (${number(confirm.units)} units, ${rupees(confirm.value)}) that "${confirm.filename}" added for ${dates(confirm.date_from, confirm.date_to)}. Every report updates at once. You can upload a corrected file afterwards. Code mappings saved from this file stay; change any wrong one in Code Mappings first.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={e => { e.preventDefault(); undo(); }} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="sales-upload-undo-confirm">
              {busy ? 'Removing…' : 'Remove rows'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SalesUploadsDialog;
